import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '@/utils'

interface SectionNavItemProps extends React.ComponentPropsWithoutRef<'button'> {
  className?: string
  children: React.ReactNode
  active?: boolean
  asChild?: boolean
}

const SectionNavItem = React.forwardRef<HTMLButtonElement, SectionNavItemProps>(
  ({ className, children, active, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const itemRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
      if (active && itemRef.current) {
        itemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
    }, [active])

    return (
      <div ref={itemRef} className="relative flex items-center flex-shrink-0">
        <Comp
          ref={ref}
          data-state={active ? 'active' : 'inactive'}
          className={cn(
            'group relative flex items-center justify-center py-2.5 text-sm font-medium transition-colors outline-none cursor-pointer whitespace-nowrap',
            'text-muted-foreground hover:text-foreground',
            'data-[state=active]:text-foreground',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm',
            className,
          )}
          {...props}
        >
          {children}
        </Comp>
        <div
          className={cn(
            'absolute left-0 right-0 bottom-[-1px] h-[2px] bg-foreground transition-all duration-200 ease-out origin-left',
            active ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0',
          )}
          aria-hidden="true"
        />
      </div>
    )
  },
)
SectionNavItem.displayName = 'SectionNavItem'

export { SectionNavItem }
